import React, { useCallback, useEffect, useRef } from 'react';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import clsx from 'clsx';
import { AVideoBrightcoveInternalProps, AVideoAspectRatio, BrightcovePlayerSuccess } from '../AVideo.types';
import { useVideoPlayState } from '../hooks/useVideoPlayState';
import * as Styled from '../AVideo.styles';

export const COMPONENT_NAME = 'a-web-ui-kit-AVideo';
export const TEST_ID = 'web-ui-kit-AVideo-brightcove';

export const AVideoBrightcove: React.FC<AVideoBrightcoveInternalProps> = ({
  videoId,
  videoAccountId,
  playerId = 'default',
  aspectRatio = AVideoAspectRatio.Ratio16x9,
  isPlaying,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  onAutoplayBlocked,
  onEnded,
  onPlayerReady,
  onPlayerError,
  className,
  'aria-label': ariaLabel = 'Video',
  ...rest
}) => {
  const { playerRef, applyPendingPlayState } = useVideoPlayState({
    isPlaying,
    onAutoplayBlocked,
  });

  // Stable refs so listeners registered once in handleSuccess always read latest values
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);
  onAutoplayBlockedRef.current = onAutoplayBlocked;

  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const onPlayerReadyRef = useRef(onPlayerReady);
  onPlayerReadyRef.current = onPlayerReady;

  // Handler refs stored so the unmount cleanup can call player.off with the
  // exact function references that were passed to player.on.
  const autoplayFailureHandlerRef = useRef<(() => void) | null>(null);
  const endedHandlerRef = useRef<(() => void) | null>(null);

  const handleSuccess = useCallback(
    (success: BrightcovePlayerSuccess) => {
      const player = success?.ref;
      if (!player) return;

      // applyPendingPlayState also sets playerRef.current
      applyPendingPlayState(player);

      if (typeof player.controls === 'function') {
        player.controls(controlsRef.current);
      }

      // @brightcove Video.js fires 'autoplay-failure' when the browser blocks autoplay
      if (typeof player.on === 'function') {
        // Defensive: remove previous listeners in case handleSuccess fires more than once
        if (typeof player.off === 'function') {
          if (autoplayFailureHandlerRef.current) {
            player.off('autoplay-failure', autoplayFailureHandlerRef.current);
          }
          if (endedHandlerRef.current) {
            player.off('ended', endedHandlerRef.current);
          }
        }

        const handleAutoplayFailure = () => {
          onAutoplayBlockedRef.current?.();
        };
        const handleEnded = () => {
          onEndedRef.current?.();
        };

        autoplayFailureHandlerRef.current = handleAutoplayFailure;
        endedHandlerRef.current = handleEnded;

        player.on('autoplay-failure', handleAutoplayFailure);
        player.on('ended', handleEnded);
      }

      onPlayerReadyRef.current?.(player);
    },
    [applyPendingPlayState]
  );

  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (player && typeof player.off === 'function') {
        if (autoplayFailureHandlerRef.current) {
          player.off('autoplay-failure', autoplayFailureHandlerRef.current);
        }
        if (endedHandlerRef.current) {
          player.off('ended', endedHandlerRef.current);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup reads from stable refs on unmount only
  }, []);

  if (!videoId?.trim() || !videoAccountId?.trim()) {
    return null;
  }

  const videoIdValue = videoId.trim();
  const accountIdValue = videoAccountId.trim();
  const playerIdValue = playerId || 'default';
  const sanitizedVideoId = videoIdValue.replace(/[^a-zA-Z0-9_-]/g, '');
  const videoElementId = `brightcove-video-${sanitizedVideoId}`;

  return (
    <Styled.BrightcoveWrapper
      $aspectRatio={aspectRatio}
      data-aspect-ratio={aspectRatio}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className)}
      {...rest}
    >
      <ReactPlayerLoader
        id={videoElementId}
        accountId={accountIdValue}
        videoId={videoIdValue}
        playerId={playerIdValue}
        onSuccess={handleSuccess}
        onFailure={onPlayerError}
        options={{
          responsive: true,
          fluid: true,
          autoplay: autoPlay,
          muted,
          loop,
        }}
        aria-label={ariaLabel}
      />
    </Styled.BrightcoveWrapper>
  );
};
